import { useState, useEffect, memo, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Heart, Eye, MapPin, Clock, Car, Gift, ShoppingCart } from 'lucide-react'
import { clsx } from 'clsx'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { formatPrice, formatRelativeTime, formatTimeRemaining, formatLocation } from '@/utils/format'
import { useSavedItems } from '@/hooks/useSavedItems'
import type { Listing } from '@/types/listing'

// Module-level cache for seller banner lookups (avoids repeat fetches per session)
const bannerCache = new Map<string, string | null>()

interface ListingCardProps {
  listing: Listing
  showSaveButton?: boolean
  compact?: boolean
  hideBanner?: boolean
  className?: string
}

export const ListingCard = memo(function ListingCard({ listing, showSaveButton = true, compact = false, hideBanner = false, className }: ListingCardProps) {
  const { isSaved, toggleSave } = useSavedItems()
  const saved = isSaved(listing.id)
  const [imgError, setImgError] = useState(false)
  const [bannerUrl, setBannerUrl] = useState<string | null>(bannerCache.get(listing.sellerId) ?? null)

  // Fetch seller banner (cached per session)
  useEffect(() => {
    if (hideBanner || compact || bannerCache.has(listing.sellerId)) return
    let cancelled = false
    getDoc(doc(db, 'users', listing.sellerId)).then((snap) => {
      const url = snap.exists() ? (snap.data().listingBannerUrl as string | undefined) || null : null
      bannerCache.set(listing.sellerId, url)
      if (!cancelled) setBannerUrl(url)
    }).catch(() => {
      bannerCache.set(listing.sellerId, null)
    })
    return () => { cancelled = true }
  }, [listing.sellerId, hideBanner, compact])

  // "New" badge: listing created within last 24 hours
  const isNew = useMemo(() => {
    if (!listing.createdAt) return false
    const created = typeof listing.createdAt.toMillis === 'function'
      ? listing.createdAt.toMillis()
      : typeof listing.createdAt === 'object' && 'seconds' in listing.createdAt
        ? (listing.createdAt as unknown as { seconds: number }).seconds * 1000
        : new Date(listing.createdAt as unknown as string).getTime()
    return Date.now() - created < 24 * 60 * 60 * 1000
  }, [listing.createdAt])

  // Auction urgency badge
  const auctionUrgency = useMemo(() => {
    if (listing.saleType !== 'auction' || !listing.expiresAt) return null
    const expires = typeof listing.expiresAt.toMillis === 'function'
      ? listing.expiresAt.toMillis()
      : typeof listing.expiresAt === 'object' && 'seconds' in listing.expiresAt
        ? (listing.expiresAt as unknown as { seconds: number }).seconds * 1000
        : new Date(listing.expiresAt as unknown as string).getTime()
    const msLeft = expires - Date.now()
    if (msLeft <= 0) return { label: 'Ended', urgent: true }
    if (msLeft < 24 * 60 * 60 * 1000) return { label: 'Ends today!', urgent: true }
    const days = Math.ceil(msLeft / (24 * 60 * 60 * 1000))
    return { label: `Ends in ${days}d`, urgent: false }
  }, [listing.saleType, listing.expiresAt])

  const isAuction = listing.saleType === 'auction'
  const isProduct = listing.saleType === 'product'
  const currentPrice = isAuction
    ? (listing as Listing & { currentBid?: number }).currentBid ||
      (listing as Listing & { startingPrice?: number }).startingPrice ||
      listing.price
    : listing.price

  const wasPrice = (listing as Listing & { wasPrice?: number }).wasPrice
  const hasDiscount = wasPrice && listing.price && wasPrice > listing.price

  const handleSaveClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    toggleSave(listing.id)
  }

  // Get type badge config
  const getTypeBadge = () => {
    switch (listing.listingType) {
      case 'vehicle':
        return { icon: <Car className="w-3 h-3" />, label: 'Vehicle', className: 'bg-slate-700 text-white' }
      case 'fundraiser':
        return { icon: <Gift className="w-3 h-3" />, label: 'Fundraiser', className: 'bg-pink-500 text-white' }
      default:
        return null
    }
  }

  const typeBadge = getTypeBadge()

  return (
    <Link
      to={`/listing/${listing.id}`}
      className={clsx('card group block hover:-translate-y-1 hover:shadow-lg transition-all duration-200', className)}
      role="article"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {listing.images?.[0] && !imgError ? (
          <img
            src={listing.images[0]}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            decoding="async"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <span className="text-4xl" aria-label="No image available" role="img">📷</span>
          </div>
        )}

        {/* Gradient overlay for text readability */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

        {/* Badges - Top Left */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
          {isNew && (
            <span className="badge bg-primary-500 text-white text-xs font-semibold animate-bounce-in">New</span>
          )}
          {auctionUrgency && (
            <span className={clsx(
              'badge text-white text-xs font-semibold flex items-center gap-1',
              auctionUrgency.urgent ? 'bg-red-500' : 'bg-amber-500'
            )}>
              <Clock className="w-3 h-3" />
              {auctionUrgency.label}
            </span>
          )}
          {isAuction && (
            <span className="badge bg-purple-500 text-white text-xs">Auction</span>
          )}
          {isProduct && (
            <span className="badge bg-blue-500 text-white text-xs flex items-center gap-1">
              <ShoppingCart className="w-3 h-3" />
              Product
            </span>
          )}
          {isProduct && listing.stock === 0 && (
            <span className="badge bg-red-500 text-white text-xs">Out of Stock</span>
          )}
          {typeBadge && (
            <span className={clsx('badge text-xs flex items-center gap-1', typeBadge.className)}>
              {typeBadge.icon}
              {typeBadge.label}
            </span>
          )}
        </div>

        {/* Save button */}
        {showSaveButton && (
          <button
            onClick={handleSaveClick}
            className={clsx(
              'absolute top-2 right-2 p-2.5 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center',
              saved
                ? 'bg-red-500 text-white'
                : 'bg-white/80 text-slate-600 hover:bg-white'
            )}
            aria-label={saved ? 'Remove from watchlist' : 'Add to watchlist'}
          >
            <Heart className={clsx('w-4 h-4', saved && 'fill-current')} />
          </button>
        )}

        {/* Auction time remaining */}
        {isAuction && listing.expiresAt && (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTimeRemaining(listing.expiresAt)}
          </div>
        )}
      </div>

      {/* Seller Banner */}
      {!hideBanner && !compact && bannerUrl && (
        <div className="border-t border-slate-100 max-h-16 overflow-hidden">
          <img
            src={bannerUrl}
            alt="Seller banner"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      {/* Content */}
      <div className={clsx('p-4', compact && 'p-3')}>
        {/* Price */}
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <div className="flex items-baseline gap-2">
            <span className={clsx('font-bold', compact ? 'text-base' : 'text-lg', listing.listingType === 'fundraiser' ? 'text-primary-600' : 'text-slate-900')}>
              {listing.listingType === 'fundraiser' ? 'Fundraiser' : formatPrice(currentPrice)}
            </span>
            {/* Was Price */}
            {!compact && hasDiscount && (
              <span className="text-sm text-slate-400 line-through">
                {formatPrice(wasPrice)}
              </span>
            )}
          </div>
          {!compact && isAuction && (listing as Listing & { bidCount?: number }).bidCount ? (
            <span className="text-xs text-slate-500">
              {(listing as Listing & { bidCount?: number }).bidCount} bids
            </span>
          ) : null}
        </div>

        {/* Title */}
        <h3 className={clsx(
          'font-medium text-slate-800 group-hover:text-primary-600 transition-colors',
          compact ? 'text-sm line-clamp-1 mb-1' : 'line-clamp-2 mb-2'
        )}>
          {listing.title}
        </h3>

        {/* Fundraiser org name - hide in compact mode */}
        {!compact && listing.listingType === 'fundraiser' && (listing as Listing & { organizationName?: string }).organizationName && (
          <p className="text-xs text-slate-500 mb-2">
            by {(listing as Listing & { organizationName?: string }).organizationName}
          </p>
        )}

        {/* Vehicle specific info - hide in compact mode */}
        {!compact && listing.listingType === 'vehicle' && (
          <div className="text-xs text-slate-500 mb-2">
            {(listing as Listing & { year?: number; make?: string }).year}{' '}
            {(listing as Listing & { make?: string }).make}{' '}
            {(listing as Listing & { odometer?: number }).odometer && (
              <span className="text-slate-400">
                • {((listing as Listing & { odometer?: number }).odometer || 0).toLocaleString()} km
              </span>
            )}
          </div>
        )}

        {/* Meta info */}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span className={clsx('truncate', compact ? 'max-w-[80px]' : 'max-w-[120px]')}>
              {formatLocation(listing.location, listing.suburb)}
            </span>
          </div>
          {!compact && (
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span>{listing.views || 0}</span>
            </div>
          )}
        </div>

        {/* Timestamp - hide in compact mode */}
        {!compact && (
          <div className="text-xs text-slate-400 mt-2">
            {formatRelativeTime(listing.createdAt)}
          </div>
        )}
      </div>
    </Link>
  )
})
